<template>
  <div class="aligo-sms">
    <v-dialog 
      v-model="dialogModel" 
      max-width="1400px" 
      height="800px"
      persistent
      scrollable
    >
      <v-card class="rounded-lg">
        <v-card-title class="d-flex justify-space-between align-center pa-4">
          <div class="d-flex align-center">
            <v-icon color="primary" class="mr-2">mdi-send</v-icon>
            <h2 class="sms-title">임금명세서 발송 관리</h2>
            <v-chip v-if="wageList && wageList.length > 0" color="info" class="ml-3">
              임금대장 {{ wageList.length }}명 로드됨
            </v-chip>
          </div>
          <v-btn 
            icon
            @click="closeDialog"
            class="btnColorError"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text class="pa-4">
          <v-tabs v-model="activeTab" class="mb-4">
            <v-tab value="employees">
              <v-icon class="mr-2">mdi-account-multiple</v-icon>
              발송대상 직원
            </v-tab>
            <v-tab value="history">
              <v-icon class="mr-2">mdi-history</v-icon>
              발송내역 조회
            </v-tab>
            <v-tab value="remain">
              <v-icon class="mr-2">mdi-counter</v-icon>
              발송가능건수 확인
            </v-tab>
          </v-tabs>

          <v-window v-model="activeTab" >
            <!-- 발송대상 직원 탭 -->
            <v-window-item value="employees">
              <v-card elevation="2">
                <v-card-title class="bg-success text-white">
                  <v-icon class="mr-2">mdi-account-cash</v-icon>
                  임금명세서 발송대상 직원 ({{ wageList.length }}명)
                </v-card-title>
                <v-card-text class="pa-4">
                  <div v-if="wageList && wageList.length > 0">
                    <!-- 전체 선택 체크박스 -->
                    <div class="d-flex justify-space-between align-center mb-4">
                      <v-checkbox
                        v-model="selectAll"
                        @update:model-value="toggleSelectAll"
                        :indeterminate="isIndeterminate"
                        label="전체 선택"
                        density="compact"
                        class="mb-0"
                      ></v-checkbox>
                      <div class="selected-info">
                        선택된 직원: {{ selectedEmployees.length }}명 / 발송가능: {{ wageList.filter(emp => hasValidPhone(emp)).length }}명
                      </div>
                    </div>

                    <!-- 직원 테이블 -->
                    <v-table class="employee-data-table" density="compact">
                      <thead>
                        <tr>
                          <th class="text-center" style="width: 80px;">선택</th>
                          <th class="text-center">이름</th>
                          <th class="text-center">직종</th>
                          <th class="text-center">전화번호</th>
                          <th class="text-center">임금합계</th>
                          <th class="text-center">시설명</th>
                          <th class="text-center">임금 연월</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="employee in wageList" :key="employee.empId || employee.empName">
                          <td class="text-center">
                            <v-checkbox
                              v-model="selectedEmployees"
                              :value="employee"
                              :disabled="!hasValidPhone(employee)"
                              @update:model-value="onEmployeeSelectionChange"
                              density="compact"
                              class="ma-0"
                            ></v-checkbox>
                          </td>
                          <td class="text-center employee-name">{{ employee.empName }}</td>
                          <td class="text-center">{{ employee.empJob }}</td>
                          <td class="text-center" :class="{ 'text-error': !hasValidPhone(employee) }">
                            {{ formatPhoneNumber(employee.empPhone || employee.phone) }}
                          </td>
                          <td class="wage-amount text-center">{{ (employee.total || 0).toLocaleString() }}원</td>
                          <td class="text-center">{{ employee.sisulName || '-' }}</td>
                          <td class="text-center">{{ store.state.AdminYYMM || '-' }}</td>
                        </tr>
                      </tbody>
                    </v-table>

                    <!-- 임금명세서 발송 버튼 -->
                    <div class="mt-4 text-center">
                      <div v-if="wageList.filter(emp => !hasValidPhone(emp)).length > 0" class="mb-3">
                        <v-alert
                          type="warning"
                          variant="outlined"
                          density="compact"
                          class="text-left"
                        >
                          <v-icon class="mr-2">mdi-alert</v-icon>
                          전화번호가 없는 직원 {{ wageList.filter(emp => !hasValidPhone(emp)).length }}명은 발송 대상에서 제외됩니다.
                        </v-alert>
                      </div>
                      <v-btn
                        class="btnColorSuccess"
                        size="large"
                        :loading="wageLoading"
                        :disabled="selectedEmployees.length === 0"
                        @click="sendWageSms"
                      >
                        <v-icon class="mr-2">mdi-send</v-icon>
                        임금명세서 발송 ({{ selectedEmployees.length }}명)
                      </v-btn>
                    </div>
                  </div>
                  <div v-else class="no-data">
                    임금대장 데이터가 없습니다.
                  </div>
                </v-card-text>
              </v-card>
            </v-window-item>

            <!-- 발송내역 조회 탭 -->
            <v-window-item value="history">
              <v-card elevation="2">
                <v-card-title class="bg-info text-white d-flex justify-space-between align-center">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2">mdi-history</v-icon>
                    임금명세서 발송내역
                  </div>
                  <v-btn 
                    class="btnColorPrimary" 
                    @click="getSmsHistory"
                    :loading="historyLoading"
                  >
                    <v-icon class="mr-1">mdi-refresh</v-icon>
                    새로고침
                  </v-btn>
                </v-card-title>
                
                <!-- 검색 필터 -->
                <v-card-text class="pa-4">
                  <!-- 날짜 선택 컨트롤 -->
                  <v-row class="mb-3" style="display: none;">
                    <v-col cols="12">
                      <div class="d-flex align-center justify-center">
                        <v-btn
                          icon
                          size="small"
                          class="btnColorPrimary mr-2"
                          @click="changeDateRange(-1)"
                        >
                          <v-icon>mdi-chevron-left</v-icon>
                        </v-btn>
                        
                        <v-chip
                          color="primary"
                          variant="elevated"
                          size="large"
                          class="mx-4 px-4"
                        >
                          <v-icon class="mr-2">mdi-calendar</v-icon>
                          {{ formatDateRange() }}
                        </v-chip>
                        
                        <v-btn
                          icon
                          size="small"
                          class="btnColorPrimary ml-2"
                          @click="changeDateRange(1)"
                        >
                          <v-icon>mdi-chevron-right</v-icon>
                        </v-btn>
                      </div>
                    </v-col>
                  </v-row>
                  
                  <!-- 빠른 날짜 선택 버튼들 -->
                  <v-row class="mb-3">
                    <v-col cols="12" class="text-center">
                      <v-btn-group density="compact" variant="outlined">
                        <v-btn
                          size="small"
                          @click="setDateRange('thisMonth')"
                          :color="selectedDateRangeType === 'thisMonth' ? 'primary' : 'default'"
                        >
                          이번 달
                        </v-btn>
                        <v-btn
                          size="small"
                          @click="setDateRange('lastMonth')"
                          :color="selectedDateRangeType === 'lastMonth' ? 'primary' : 'default'"
                        >
                          지난 달
                        </v-btn>
                        <v-btn
                          size="small"
                          @click="setDateRange('last3Months')"
                          :color="selectedDateRangeType === 'last3Months' ? 'primary' : 'default'"
                        >
                          최근 3개월
                        </v-btn>
                        <v-btn
                          size="small"
                          @click="setDateRange('today')"
                          :color="selectedDateRangeType === 'today' ? 'primary' : 'default'"
                        >
                          오늘
                        </v-btn>
                      </v-btn-group>
                    </v-col>
                  </v-row>
                  
                  <v-row>
                    <v-col cols="12" md="3">
                      <v-text-field
                        v-model="historyFilter.start_date"
                        label="조회시작일자"
                        type="date"
                        density="compact"
                        variant="outlined"
                        @update:model-value="onDateInputChange"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-text-field
                        v-model="historyFilter.limit_day"
                        label="조회마감일자"
                        type="date"
                        density="compact"
                        variant="outlined"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="2">
                      <v-select
                        v-model="historyFilter.filterByEmployees"
                        :items="[
                          { title: '직원들만 조회', value: true },
                          { title: '전체 조회', value: false }
                        ]"
                        label="조회 범위"
                        density="compact"
                        variant="outlined"
                      ></v-select>
                    </v-col>
                    <v-col cols="12" md="2">
                      <v-select
                        v-model="historyFilter.page_size"
                        :items="[10, 30, 50, 100]"
                        label="페이지당 건수"
                        density="compact"
                        variant="outlined"
                      ></v-select>
                    </v-col>
                    <v-col cols="12" md="1">
                      <v-text-field
                        v-model="historyFilter.page"
                        label="페이지"
                        type="number"
                        min="1"
                        density="compact"
                        variant="outlined"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="1">
                      <v-btn
                        class="btnColorSuccess"
                        @click="getSmsHistory"
                        :loading="historyLoading"
                        block
                      >
                        <v-icon class="mr-1">mdi-magnify</v-icon>
                        조회
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>

                <v-card-text class="pa-0">
                  <div v-if="smsHistory.length > 0">
                    <v-table class="history-data-table" density="compact">
                      <thead>
                        <tr>
                          <th class="text-center">메시지ID</th>
                          <th class="text-center">발송일시</th>
                          <th class="text-center">발신자</th>
                          <th class="text-center">수신자</th>
                          <th class="text-center">메시지타입</th>
                          <th class="text-center">메시지</th>
                          <th class="text-center">발송건수</th>
                          <th class="text-center">실패건수</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="item in smsHistory" :key="item.mid">
                          <td class="text-center font-weight-medium">{{ item.mid }}</td>
                          <td class="text-center">{{ formatDateTime(item.reg_date) }}</td>
                          <td class="text-center">{{ item.sender }}</td>
                          <td class="text-center receiver-cell">
                            <div v-if="item.receivers && item.receivers.length > 0" class="receiver-content">
                              <span v-if="item.receivers.length <= 3">{{ item.receivers.join(', ') }}</span>
                              <span v-else>
                                {{ item.receivers.slice(0, 2).join(', ') }} 외 {{ item.receivers.length - 2 }}명
                              </span>
                            </div>
                            <div v-else class="text-grey-500">-</div>
                          </td>
                          <td class="text-center">
                            <v-chip 
                              :color="getTypeColor(item.type)" 
                              size="small"
                              variant="flat"
                              class="font-weight-bold"
                            >
                              {{ item.type }}
                            </v-chip>
                          </td>
                          <td class="message-cell">
                            <v-tooltip 
                              location="top"
                              max-width="400px"
                            >
                              <template v-slot:activator="{ props }">
                                <div v-bind="props" class="message-content" style="cursor: help;">
                                  {{ item.msg && item.msg.length > 30 ? item.msg.substring(0, 30) + '...' : item.msg }}
                                </div>
                              </template>
                              <div class="message-tooltip">
                                <div class="tooltip-header">메시지 전체 내용</div>
                                <div class="tooltip-message">{{ item.msg }}</div>
                              </div>
                            </v-tooltip>
                          </td>
                          <td class="text-center">
                            <v-chip color="info" size="small" variant="outlined">
                              {{ item.sms_count }}건
                            </v-chip>
                          </td>
                          <td class="text-center">
                            <v-tooltip 
                              v-if="item.fail_count > 0 && item.failReasons && item.failReasons.length > 0"
                              location="top"
                              max-width="300px"
                            >
                              <template v-slot:activator="{ props }">
                                <v-chip 
                                  v-bind="props"
                                  :color="item.fail_count > 0 ? 'error' : 'success'" 
                                  size="small"
                                  variant="flat"
                                  style="cursor: help;"
                                >
                                  {{ item.fail_count }}건
                                </v-chip>
                              </template>
                              <div class="fail-reasons-tooltip">
                                <div class="tooltip-header">실패 사유</div>
                                <div v-for="(reason, index) in item.failReasons" :key="index" class="tooltip-item">
                                  • {{ reason }}
                                </div>
                              </div>
                            </v-tooltip>
                            <v-chip 
                              v-else
                              :color="item.fail_count > 0 ? 'error' : 'success'" 
                              size="small"
                              variant="flat"
                            >
                              {{ item.fail_count }}건
                            </v-chip>
                          </td>
                        </tr>
                      </tbody>
                    </v-table>
                  </div>
                  <div v-else-if="!historyLoading" class="no-data">
                    <v-icon size="80" color="grey-400" class="mb-4">mdi-history</v-icon>
                    <p class="text-h6 text-grey-600">임금명세서 발송내역이 없습니다.</p>
                  </div>
                  <div v-else class="no-data">
                    <v-progress-circular indeterminate color="primary" class="mb-4"></v-progress-circular>
                    <p class="text-h6 text-grey-600">발송내역을 조회하고 있습니다...</p>
                  </div>
                  
                  <!-- 페이지네이션 정보 -->
                  <div v-if="historyInfo && historyInfo.total_count && historyInfo.total_count > 0" class="pa-4">
                    <v-row align="center">
                      <v-col>
                        <div class="text-body-2 text-grey-600">
                          현재 페이지: {{ historyInfo.current_page || 1 }}페이지 ({{ historyInfo.total_count }}건 조회됨)
                          <span v-if="historyInfo.next_yn === 'Y'" class="text-info ml-2">다음 페이지 있음</span>
                        </div>
                      </v-col>
                      <v-col cols="auto">
                        <v-btn-group density="compact">
                          <v-btn 
                            :disabled="(historyInfo.current_page || 1) <= 1"
                            @click="changePage((historyInfo.current_page || 1) - 1)"
                            size="small"
                          >
                            <v-icon>mdi-chevron-left</v-icon>
                          </v-btn>
                          <v-btn 
                            :disabled="historyInfo.next_yn !== 'Y'"
                            @click="changePage((historyInfo.current_page || 1) + 1)"
                            size="small"
                          >
                            <v-icon>mdi-chevron-right</v-icon>
                          </v-btn>
                        </v-btn-group>
                      </v-col>
                    </v-row>
                  </div>
                </v-card-text>
              </v-card>
            </v-window-item>

            <!-- 발송가능건수 확인 탭 -->
            <v-window-item value="remain">
              <v-card elevation="2">
                <v-card-title class="bg-primary text-white d-flex justify-space-between align-center">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2">mdi-counter</v-icon>
                    발송가능건수
                  </div>
                  <v-btn 
                    class="btnColorSuccess" 
                    @click="getSmsRemain"
                    :loading="remainLoading"
                  >
                    <v-icon class="mr-1">mdi-refresh</v-icon>
                    새로고침
                  </v-btn>
                </v-card-title>
                <v-card-text class="pa-6">
                  <div v-if="remainLoading" class="no-data">
                    <v-progress-circular indeterminate color="primary" class="mb-4"></v-progress-circular>
                    <p class="text-h6 text-grey-600">발송가능건수를 조회하고 있습니다...</p>
                  </div>
                  <div v-else-if="remainInfo" class="remain-cards-container">
                    <v-row>
                      <v-col cols="12" md="4">
                        <v-card class="remain-card sms-card" elevation="4">
                          <v-card-text class="text-center pa-6">
                            <v-icon color="primary" size="60" class="mb-4">mdi-message-text</v-icon>
                            <h3 class="text-h4 font-weight-bold primary--text mb-2">{{ remainInfo.SMS_CNT || 0 }}</h3>
                            <p class="text-h6 text-grey-600 mb-0">SMS 잔여건수</p>
                          </v-card-text>
                        </v-card>
                      </v-col>
                      
                      <v-col cols="12" md="4">
                        <v-card class="remain-card lms-card" elevation="4">
                          <v-card-text class="text-center pa-6">
                            <v-icon color="secondary" size="60" class="mb-4">mdi-message-text-outline</v-icon>
                            <h3 class="text-h4 font-weight-bold secondary--text mb-2">{{ remainInfo.LMS_CNT || 0 }}</h3>
                            <p class="text-h6 text-grey-600 mb-0">LMS 잔여건수</p>
                          </v-card-text>
                        </v-card>
                      </v-col>
                      
                      <v-col cols="12" md="4">
                        <v-card class="remain-card mms-card" elevation="4">
                          <v-card-text class="text-center pa-6">
                            <v-icon color="info" size="60" class="mb-4">mdi-image-text</v-icon>
                            <h3 class="text-h4 font-weight-bold info--text mb-2">{{ remainInfo.MMS_CNT || 0 }}</h3>
                            <p class="text-h6 text-grey-600 mb-0">MMS 잔여건수</p>
                          </v-card-text>
                        </v-card>
                      </v-col>
                    </v-row>
                    
                  
                  </div>
                  <div v-else class="no-data">
                    <v-icon size="80" color="grey-400" class="mb-4">mdi-counter</v-icon>
                    <p class="text-h6 text-grey-600">발송가능건수 데이터가 없습니다.</p>
                    <v-btn class="btnColorPrimary mt-4" @click="getSmsRemain">
                      <v-icon class="mr-2">mdi-refresh</v-icon>
                      다시 조회
                    </v-btn>
                  </div>
                </v-card-text>
              </v-card>
            </v-window-item>
          </v-window>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed } from 'vue';
import { useStore } from 'vuex';
import axios from 'axios';
import '@/assets/styles/components/sms.scss';

// 다이얼로그 모델을 독립적으로 관리
const dialogModel = ref(false);
const activeTab = ref('employees');

// 인터페이스 정의
interface SmsHistoryItem {
  mid: string;
  type: string;
  sender: string;
  sms_count: string;
  reserve_state: string;
  msg: string;
  fail_count: number;
  reg_date: string;
  reserve: string;
  receivers?: string[];
  failReasons?: string[];
}

interface RemainInfo {
  SMS_CNT?: number;
  LMS_CNT?: number;
  MMS_CNT?: number;
}

interface Employee {
  empId?: string;
  empName: string;
  empJob: string;
  empPhone?: string;
  phone?: string;
  total?: number;
  sisulName?: string;
  ym?: string;
  [key: string]: any;
}

const store = useStore();

// 로컬 날짜를 YYYY-MM-DD 형식으로 변환하는 함수 (UTC 시간대 문제 해결)
const formatLocalDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// wageList 데이터
const wageList = ref<Employee[]>([]);

// 로딩 상태
const remainLoading = ref(false);
const historyLoading = ref(false);
const wageLoading = ref(false);

const selectedEmployees = ref<Employee[]>([]);
const selectAll = ref(false);

// 발송 내역
const smsHistory = ref<SmsHistoryItem[]>([]);
const remainInfo = ref<RemainInfo | null>(null);

// 발송내역 필터 및 페이지네이션
const historyFilter = ref({
  page: 1,
  page_size: 30,
  start_date: (() => {
    const today = new Date();
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    return formatLocalDate(startOfMonth);
  })(),
  limit_day: (() => {
    const today = new Date();
    const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return formatLocalDate(endOfMonth);
  })(),
  filterByEmployees: true // 기본값: 직원들만 조회
});

// 현재 표시 중인 연월을 추적하는 변수 추가
const currentDisplayYear = ref(new Date().getFullYear());
const currentDisplayMonth = ref(new Date().getMonth()); // 0-11

// 현재 선택된 날짜 범위 타입을 추적하는 변수 추가
const selectedDateRangeType = ref('thisMonth');

const historyInfo = ref<{
  total_count?: number;
  current_page?: number;
  next_yn?: string;
} | null>(null);

// 전체선택 상태 계산
const isIndeterminate = computed(() => {
  const validEmployees = wageList.value.filter(emp => hasValidPhone(emp));
  return selectedEmployees.value.length > 0 && selectedEmployees.value.length < validEmployees.length;
});

// 직원들의 전화번호 목록 추출
const employeePhoneNumbers = computed(() => {
  if (!wageList.value || wageList.value.length === 0) return [];
  
  return wageList.value
    .map(emp => emp.empPhone || emp.phone)
    .filter((phone): phone is string => phone != null && phone.trim() !== '')
    .map(phone => {
      // 전화번호 정규화 (하이픈 제거)
      return phone.replace(/\D/g, '');
    })
    .filter((phone, index, array) => array.indexOf(phone) === index); // 중복 제거
});

// 전화번호 포맷팅 함수
const formatPhoneNumber = (phone?: string) => {
  if (!phone) return '전화번호 없음';
  // 전화번호 포맷팅 (예: 01012345678 -> 010-1234-5678)
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 11) {
    return cleaned.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3');
  } else if (cleaned.length === 10) {
    return cleaned.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
  }
  return phone;
};

// 개별 직원 선택 변경 시 호출
const onEmployeeSelectionChange = () => {
  // 전화번호가 있는 직원 수 기준으로 전체선택 상태 업데이트
  const validEmployees = wageList.value.filter(emp => hasValidPhone(emp));
  if (selectedEmployees.value.length === validEmployees.length) {
    selectAll.value = true;
  } else {
    selectAll.value = false;
  }
};

// 다이얼로그 열기 (외부에서 호출)
const openDialog = (wage?: Employee[]) => {
  if (wage && wage.length > 0) {
    wageList.value = wage;
  }
  
  // 현재 날짜를 기준으로 초기화
  const today = new Date();
  currentDisplayYear.value = today.getFullYear();
  currentDisplayMonth.value = today.getMonth();
  
  // 이번 달의 첫째 날과 마지막 날로 설정
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  
  historyFilter.value.start_date = formatLocalDate(startOfMonth);
  historyFilter.value.limit_day = formatLocalDate(endOfMonth);
  historyFilter.value.page = 1; // 페이지도 초기화
  
  // 날짜 범위 타입을 이번 달로 초기화
  selectedDateRangeType.value = 'thisMonth';
  dialogModel.value = true;
  // 첫 번째 탭으로 이동
  activeTab.value = 'employees';
  
  // 다이얼로그 열릴 때 자동으로 발송내역과 발송가능건수 조회
  setTimeout(() => {
    getSmsHistory();
    getSmsRemain();
  }, 100);
};

// 다이얼로그 닫기
const closeDialog = () => {
  dialogModel.value = false;
  // 상태 초기화
  selectedEmployees.value = [];
  selectAll.value = false;
  activeTab.value = 'employees';
  
  // 날짜 관련 상태도 초기화
  const today = new Date();
  currentDisplayYear.value = today.getFullYear();
  currentDisplayMonth.value = today.getMonth();
  selectedDateRangeType.value = 'thisMonth';
  
  // 히스토리 필터 초기화
  historyFilter.value.page = 1;
  smsHistory.value = [];
  historyInfo.value = null;
  remainInfo.value = null;
};

// 날짜시간 포맷팅 함수
const formatDateTime = (dateStr: string) => {
  if (!dateStr) return '-';
  // YYYY-MM-DD HH:mm:ss 형식으로 변환
  if (dateStr.length === 14) {
    // YYYYMMDDHHMMSS -> YYYY-MM-DD HH:mm:ss
    return `${dateStr.substr(0,4)}-${dateStr.substr(4,2)}-${dateStr.substr(6,2)} ${dateStr.substr(8,2)}:${dateStr.substr(10,2)}:${dateStr.substr(12,2)}`;
  }
  return dateStr;
};

// SMS 상세 정보 표시
const showSmsDetail = (item: SmsHistoryItem) => {
  store.commit('showSnackbar', {
    text: `메시지 ID: ${item.mid}\n발송타입: ${item.type}\n발송건수: ${item.sms_count}건\n실패건수: ${item.fail_count}건\n메시지: ${item.msg}`,
    color: 'info'
  });
  
  // 실제 상세 조회 API 호출
  getSmsDetail(item.mid);
};

// 메시지 타입 색상 반환 함수
const getTypeColor = (type: string) => {
  switch (type) {
    case 'SMS':
      return 'primary';
    case 'LMS':
      return 'secondary';
    case 'MMS':
      return 'info';
    default:
      return 'grey';
  }
};

// 상태 색상 반환 함수
const getStatusColor = (status: string) => {
  switch (status) {
    case '성공':
    case 'SUCCESS':
      return 'success';
    case '실패':
    case 'FAILED':
      return 'error';
    case '대기':
    case 'PENDING':
      return 'warning';
    default:
      return 'grey';
  }
};

// 전체 선택/해제
const toggleSelectAll = () => {
  if (selectAll.value) {
    // 전화번호가 있는 직원만 선택
    selectedEmployees.value = wageList.value.filter(emp => hasValidPhone(emp));
  } else {
    selectedEmployees.value = [];
  }
};

// 임금명세서 기반 SMS 발송
const sendWageSms = async () => {
  if (selectedEmployees.value.length === 0) {
    store.commit('showSnackbar', {
      text: '발송할 직원을 선택해주세요.',
      color: 'error'
    });
    return;
  }

  // 전화번호가 없는 직원 확인
  const employeesWithoutPhone = selectedEmployees.value.filter(emp => !emp.empPhone && !emp.phone);
  if (employeesWithoutPhone.length > 0) {
    store.commit('showSnackbar', {
      text: `전화번호가 없는 직원이 ${employeesWithoutPhone.length}명 있습니다. 확인해주세요.`,
      color: 'warning'
    });
  }

  wageLoading.value = true;
  try {
    const requestData = {
      employees: selectedEmployees.value,
      sisulName: store.getters.AdminSisulName,
      sisulCode: store.getters.AdminSisulCode
    };
    
    const response = await axios.post('/api/sms/send-wage', requestData);
    
    if (response.data.code === '1') {
      const { successCount, failCount, totalCount } = response.data.data;
      store.commit('showSnackbar', {
        text: `임금명세서 발송 완료 - 성공: ${successCount}건, 실패: ${failCount}건`,
        color: 'success'
      });
      
      // 선택 초기화
      selectedEmployees.value = [];
      selectAll.value = false;
      
      // 발송 내역 탭으로 이동하고 새로고침
      activeTab.value = 'history';
      getSmsHistory();
    } else {
      store.commit('showSnackbar', {
        text: response.data.message || '임금명세서 발송에 실패했습니다.',
        color: 'error'
      });
    }
  } catch (error) {
    console.error('임금명세서 발송 오류:', error);
    console.error('에러 응답:', (error as any).response?.data);
    store.commit('showSnackbar', {
      text: '임금명세서 발송 중 오류가 발생했습니다.',
      color: 'error'
    });
  } finally {
    wageLoading.value = false;
  }
};

// 페이지 변경 함수
const changePage = (page: number) => {
  historyFilter.value.page = page;
  getSmsHistory();
};

// 발송 내역 조회
const getSmsHistory = async () => {
  historyLoading.value = true;
  try {
    // 날짜 형식 변환 (YYYY-MM-DD -> YYYYMMDD)
    const formatDate = (dateStr: string) => {
      if (!dateStr) return '';
      return dateStr.replace(/-/g, '');
    };

    const params = {
      page: historyFilter.value.page,
      page_size: historyFilter.value.page_size,
      ...(historyFilter.value.start_date && { start_date: formatDate(historyFilter.value.start_date) }),
      ...(historyFilter.value.limit_day && { limit_day: formatDate(historyFilter.value.limit_day) })
    };


    const response = await axios.get('/api/sms/history', { params });
    
    
    // Aligo API 응답 구조에 맞게 수정
    if (response.data.result_code === 1) {
      const historyList = response.data.list || [];
      
      // 모든 메시지의 상세정보를 병렬로 조회
      if (historyList.length > 0) {
        
        const detailPromises = historyList.map(async (item: SmsHistoryItem) => {
          try {
            const detailResponse = await axios.get(`/api/sms/detail/${item.mid}`);
            if (detailResponse.data.list && detailResponse.data.list.length > 0) {
              // 수신자 목록 추출
              const receivers = detailResponse.data.list.map((detail: any) => detail.receiver || detail.phone);
              
              // 실패 사유 추출 (sms_state가 성공이 아닌 경우들)
              const failReasons = detailResponse.data.list
                .filter((detail: any) => detail.sms_state && detail.sms_state !== '전송성공' && detail.sms_state !== '성공')
                .map((detail: any) => detail.sms_state)
                .filter((reason: string, index: number, array: string[]) => array.indexOf(reason) === index); // 중복 제거
              
              return { 
                ...item, 
                receivers: receivers.filter(Boolean),
                failReasons: failReasons
              };
            }
            return { ...item, receivers: [], failReasons: [] };
          } catch (error) {
            console.error(`메시지 ${item.mid} 상세조회 오류:`, error);
            return { ...item, receivers: [], failReasons: [] };
          }
        });
        
        // 모든 상세조회 완료 대기
        const enrichedHistory = await Promise.all(detailPromises);
        
        // 직원 필터링 적용
        if (historyFilter.value.filterByEmployees && employeePhoneNumbers.value.length > 0) {
          const filteredHistory = enrichedHistory.filter(item => {
            if (!item.receivers || item.receivers.length === 0) return false;
            
            // 수신자 중 직원 전화번호가 포함되어 있는지 확인
            return item.receivers.some((receiver: string) => {
              const normalizedReceiver = receiver.replace(/\D/g, '');
              return employeePhoneNumbers.value.includes(normalizedReceiver);
            });
          });
          smsHistory.value = filteredHistory;
        } else {
          smsHistory.value = enrichedHistory;
        }
        
      } else {
        smsHistory.value = historyList;
      }
      
      historyInfo.value = {
        total_count: historyList.length,
        current_page: historyFilter.value.page,
        next_yn: response.data.next_yn || 'N'
      };
      
      store.commit('showSnackbar', {
        text: `발송 내역을 조회했습니다. (총 ${historyInfo.value.total_count}건)`,
        color: 'info'
      });
    } else {
      smsHistory.value = [];
      historyInfo.value = null;
      store.commit('showSnackbar', {
        text: response.data.message || '발송 내역 조회에 실패했습니다.',
        color: 'error'
      });
    }
  } catch (error) {
    console.error('발송 내역 조회 오류:', error);
    smsHistory.value = [];
    historyInfo.value = null;
    store.commit('showSnackbar', {
      text: '발송 내역 조회 중 오류가 발생했습니다.',
      color: 'error'
    });
  } finally {
    historyLoading.value = false;
  }
};

// 발송 가능 건수 조회
const getSmsRemain = async () => {
  remainLoading.value = true;
  try {
    
    const response = await axios.get('/api/sms/remain');
    
    
    // Aligo API 응답 구조 확인 후 처리
    if (response.data.result_code === 1 || response.data.code === '1') {
      // 응답 데이터 구조에 따라 처리
      remainInfo.value = {
        SMS_CNT: response.data.SMS_CNT || response.data.sms_count || 0,
        LMS_CNT: response.data.LMS_CNT || response.data.lms_count || 0,
        MMS_CNT: response.data.MMS_CNT || response.data.mms_count || 0
      };
      
      
      store.commit('showSnackbar', {
        text: '발송 가능 건수를 조회했습니다.',
        color: 'info'
      });
    } else {
      // 응답이 실패한 경우에도 테스트용 데이터 표시
      remainInfo.value = {
        SMS_CNT: 100,
        LMS_CNT: 50,
        MMS_CNT: 25
      };
      
      store.commit('showSnackbar', {
        text: response.data.message || '발송 가능 건수 조회에 실패했습니다. (테스트 데이터 표시)',
        color: 'warning'
      });
    }
  } catch (error) {
    console.error('발송 가능 건수 조회 오류:', error);
    console.error('에러 상세:', (error as any).response?.data);
    
    // 오류 시에도 테스트용 데이터 표시
    remainInfo.value = {
      SMS_CNT: 100,
      LMS_CNT: 50,
      MMS_CNT: 25
    };
    
    store.commit('showSnackbar', {
      text: '발송 가능 건수 조회 중 오류가 발생했습니다. (테스트 데이터 표시)',
      color: 'warning'
    });
  } finally {
    remainLoading.value = false;
  }
};

// SMS 상세 조회
const getSmsDetail = async (mid: string) => {
  try {
    const response = await axios.get(`/api/sms/detail/${mid}`);
    if (response.data.list) {
      store.commit('showSnackbar', {
        text: '상세 정보를 조회했습니다.',
        color: 'info'
      });
      // 여기서 상세 정보를 다이얼로그로 표시할 수 있습니다.
    } else {
      store.commit('showSnackbar', {
        text: '상세 정보 조회에 실패했습니다.',
        color: 'error'
      });
    }
  } catch (error) {
    console.error('SMS 상세 조회 오류:', error);
    store.commit('showSnackbar', {
      text: '상세 정보 조회 중 오류가 발생했습니다.',
      color: 'error'
    });
  }
};

// 외부에서 호출할 수 있도록 expose
defineExpose({
  openDialog
});

// 날짜 관련 함수들
const formatDateRange = () => {
  return `${currentDisplayYear.value}-${String(currentDisplayMonth.value + 1).padStart(2, '0')}`;
};

// 한 달 단위로 날짜 범위 변경
const changeDateRange = (monthOffset: number) => {
  // 현재 표시 중인 연월을 기준으로 이동
  const newMonth = currentDisplayMonth.value + monthOffset;
  const newYear = currentDisplayYear.value + Math.floor(newMonth / 12);
  const adjustedMonth = ((newMonth % 12) + 12) % 12;
  
  // 현재 표시 연월 업데이트
  currentDisplayYear.value = newYear;
  currentDisplayMonth.value = adjustedMonth;
  
  // 해당 월의 첫째 날과 마지막 날 계산
  const startOfMonth = new Date(newYear, adjustedMonth, 1);
  const endOfMonth = new Date(newYear, adjustedMonth + 1, 0);
  
  // 날짜 필터 업데이트
  historyFilter.value.start_date = formatLocalDate(startOfMonth);
  historyFilter.value.limit_day = formatLocalDate(endOfMonth);
  
  // 날짜 범위 타입 초기화 (수동으로 변경했으므로)
  selectedDateRangeType.value = '';
  
  // 자동으로 조회
  getSmsHistory();
};

// 빠른 날짜 설정
const setDateRange = (type: string) => {
  const today = new Date();
  let startDate: Date;
  let endDate: Date;
  let targetYear: number;
  let targetMonth: number;
  
  // 선택된 날짜 범위 타입 업데이트
  selectedDateRangeType.value = type;
  
  switch (type) {
    case 'today':
      startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      targetYear = today.getFullYear();
      targetMonth = today.getMonth();
      break;
      
    case 'thisMonth':
      startDate = new Date(today.getFullYear(), today.getMonth(), 1);
      endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      targetYear = today.getFullYear();
      targetMonth = today.getMonth();
      break;
      
    case 'lastMonth':
      const lastMonth = today.getMonth() - 1;
      const lastMonthYear = lastMonth < 0 ? today.getFullYear() - 1 : today.getFullYear();
      const adjustedLastMonth = lastMonth < 0 ? 11 : lastMonth;
      
      startDate = new Date(lastMonthYear, adjustedLastMonth, 1);
      endDate = new Date(lastMonthYear, adjustedLastMonth + 1, 0);
      targetYear = lastMonthYear;
      targetMonth = adjustedLastMonth;
      break;
      
    case 'last3Months':
      const threeMonthsAgo = today.getMonth() - 2;
      const threeMonthsAgoYear = threeMonthsAgo < 0 ? today.getFullYear() - 1 : today.getFullYear();
      const adjustedThreeMonthsAgo = threeMonthsAgo < 0 ? 12 + threeMonthsAgo : threeMonthsAgo;
      
      startDate = new Date(threeMonthsAgoYear, adjustedThreeMonthsAgo, 1);
      endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      targetYear = threeMonthsAgoYear;
      targetMonth = adjustedThreeMonthsAgo;
      break;
      
    default:
      return;
  }
  
  // 현재 표시 연월 업데이트
  currentDisplayYear.value = targetYear;
  currentDisplayMonth.value = targetMonth;
  
  // 날짜 필터 업데이트
  historyFilter.value.start_date = formatLocalDate(startDate);
  historyFilter.value.limit_day = formatLocalDate(endDate);

  // 자동으로 조회
  getSmsHistory();
};

// 현재 월인지 확인
const isCurrentMonth = () => {
  const today = new Date();
  return today.getFullYear() === currentDisplayYear.value && 
         today.getMonth() === currentDisplayMonth.value;
};

// 날짜 입력 필드가 변경될 때 현재 표시 연월도 업데이트
const onDateInputChange = () => {
  if (historyFilter.value.start_date) {
    const startDate = new Date(historyFilter.value.start_date + 'T00:00:00'); // 로컬 시간으로 파싱
    currentDisplayYear.value = startDate.getFullYear();
    currentDisplayMonth.value = startDate.getMonth();
    
    // 시작일이 변경되면 해당 월의 마지막 날로 마감일 자동 설정
    const endOfMonth = new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0);
    historyFilter.value.limit_day = formatLocalDate(endOfMonth);
    
    // 날짜 범위 타입 초기화 (수동으로 변경했으므로)
    selectedDateRangeType.value = '';

  }
};

// 전화번호가 있는지 확인하는 함수
const hasValidPhone = (employee: Employee) => {
  return employee.empPhone || employee.phone;
};
</script>
